// API client pour les utilisateurs
export interface CreateUserData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: 'admin' | 'agency_owner' | 'agent';
  agency_id?: number;
}

export interface UpdateUserData extends Partial<Omit<CreateUserData, 'password'>> {
  password?: string;
  status?: 'active' | 'inactive' | 'suspended';
}

export interface LoginData {
  email: string;
  password: string;
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: 'admin' | 'agency_owner' | 'agent';
  status: 'active' | 'inactive' | 'suspended';
  agency_id?: number;
  agency_name?: string;
  created_at: string;
  updated_at: string;
}

class UserAPI {
  private baseUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'}/users`;

  async getAll(filters?: {
    agency_id?: number;
    role?: string;
    status?: string;
  }) {
    const params = new URLSearchParams();
    if (filters?.agency_id) params.append('agency_id', filters.agency_id.toString());
    if (filters?.role) params.append('role', filters.role);
    if (filters?.status) params.append('status', filters.status);

    const url = params.toString() ? `${this.baseUrl}?${params}` : this.baseUrl;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch users');
    return response.json();
  }

  async create(data: CreateUserData) {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create user');
    }
    return response.json();
  }

  async update(id: number, data: UpdateUserData) {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update user');
    }
    return response.json();
  }

  async delete(id: number) {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete user');
    }
    return response.json();
  }

  async login(data: LoginData) {
    console.log('🌐 UserAPI.login appelé avec:', {
      email: data.email,
      password: data.password ? '***' : 'vide',
      url: `${this.baseUrl}/login`
    });
    
    try {
      console.log('📡 Envoi de la requête POST...');
      const response = await fetch(`${this.baseUrl}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      console.log('📋 Réponse reçue:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      if (!response.ok) {
        let error;
        try {
          const responseText = await response.text();
          console.log('📄 Contenu de la réponse d\'erreur:', responseText);
          error = responseText ? JSON.parse(responseText) : { error: 'Réponse vide' };
        } catch (parseError) {
          console.error('❌ Erreur de parsing de la réponse:', parseError);
          error = { error: 'Réponse invalide du serveur' };
        }
        console.error('❌ Erreur API:', error);

        // Créer une erreur personnalisée qui contient les données de la réponse
        const customError: any = new Error(error.error || error.message || 'Failed to login');
        customError.response = { data: error, status: response.status };
        throw customError;
      }
      
      const result = await response.json();
      console.log('✅ Connexion réussie, données utilisateur:', {
        userId: result.user?.id,
        email: result.user?.email,
        role: result.user?.role,
        agencyId: result.user?.agency_id
      });
      return result;
    } catch (networkError) {
      console.error('💥 Erreur réseau ou serveur:', networkError);
      throw networkError;
    }
  }

  async suspend(id: number) {
    return this.update(id, { status: 'suspended' });
  }

  async activate(id: number) {
    return this.update(id, { status: 'active' });
  }

  async changeRole(id: number, role: 'admin' | 'agency_owner' | 'agent') {
    return this.update(id, { role });
  }
}

export const userAPI = new UserAPI();